/**
 * R69 · TaskBillingView — finance.view-gated billing surface for tasks.
 *
 * Renders billable tasks with checkboxes for `billed` and `paid` so
 * finance can track invoice lifecycle at the task level. Includes
 * status filter chips (All / Unbilled / Billed / Unpaid / Paid), a
 * summary header (counts + hours), and a compact table optimised for
 * reconciliation.
 *
 * Data source: GET /workspaces/{ws}/tasks-billing
 * Update: PATCH /tasks/{id} { billed, paid }
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtRelative } from "@/lib/format";
import { apiErrorMessage } from "@/lib/apiError";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Receipt, DollarSign, CheckCircle2, Download, Mail, Send, Loader2, MessageSquare, MessageSquarePlus, ChevronRight, ChevronDown, ChevronsDown, ChevronsUp, Copy, X } from "lucide-react";
import BillingNotesDialog from "@/components/tasks/BillingNotesDialog";
import FinanceReportDialog from "@/components/tasks/FinanceReportDialog";
import { CURRENCIES, symbolFor } from "@/components/tasks/TaskFinanceDialog";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";

const STATUS_OPTS = ["pending_approval", "open", "in_progress", "review", "blocked", "done", "cancelled"];

// Feb 2026 R293t · Resizable columns for the Billing table. Widths are
// stored (in px) per workspace in localStorage so a user's layout
// survives reloads. A thin drag-handle on the right edge of each
// header captures mousedown and updates the width live via
// document-level listeners (double-click to reset a single column).
const BILLING_COL_KEYS = [
  "select", "task", "status", "assignee", "package",
  "est", "hours", "age", "ready", "billed", "paid",
  "ccy", "est_amt", "paid_amt", "balance", "bill_month",
  "note", "by_when", "notes",
];
const BILLING_COL_DEFAULTS = {
  select: 32, task: 300, status: 144, assignee: 120, package: 120,
  est: 72, hours: 72, age: 64, ready: 76, billed: 80, paid: 72,
  ccy: 72, est_amt: 108, paid_amt: 108, balance: 108, bill_month: 152,
  note: 200, by_when: 128, notes: 76,
};
const BILLING_COL_META = {
  select:    { label: "",          align: "center", title: "" },
  task:      { label: "Task",      align: "left",   title: "" },
  status:    { label: "Status",    align: "left",   title: "" },
  assignee:  { label: "Assignee",  align: "left",   title: "" },
  package:   { label: "Package",   align: "left",   title: "Package this task was auto-generated from" },
  est:       { label: "Est.",      align: "right",  title: "Estimated effort in hours" },
  hours:     { label: "Hours",     align: "right",  title: "Total logged hours (own + subtasks + manual)" },
  age:       { label: "Age",       align: "center", title: "How many days since the task became ready to bill (completed / created)" },
  ready:     { label: "Ready",     align: "center", title: "Ready to bill — PM has marked the work complete and handed off to Finance" },
  billed:    { label: "Billed",    align: "center", title: "" },
  paid:      { label: "Paid",      align: "center", title: "" },
  ccy:       { label: "Ccy",       align: "center", title: "Task currency" },
  est_amt:   { label: "Est. amt",  align: "right",  title: "Estimated billable amount for this task" },
  paid_amt:  { label: "Paid amt",  align: "right",  title: "Paid amount against this task" },
  balance:   { label: "Balance",   align: "right",  title: "Balance = Estimated − Paid" },
  bill_month:{ label: "Bill month",align: "center", title: "Accounting month this task's billing lands in — auto-fills from Billed date, override if needed" },
  note:      { label: "Last note", align: "left",   title: "Latest Accounts note (private, only visible from Billing tab)" },
  by_when:   { label: "By / when", align: "left",   title: "" },
  notes:     { label: "Notes",     align: "center", title: "" },
};

// Thin drag handle rendered absolutely on the right edge of a <th>.
// Invisible until hovered; double-click resets this one column to its
// default width.
function BillingResizeHandle({ onMouseDown, onReset, colKey }) {
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      onMouseDown={onMouseDown}
      onClick={(e) => { e.stopPropagation(); }}
      onDoubleClick={(e) => { e.stopPropagation(); onReset?.(); }}
      className="absolute top-0 right-0 h-full w-[6px] cursor-col-resize select-none z-20 hover:bg-sky-400/60 active:bg-sky-500/80"
      data-testid={`billing-col-resize-${colKey}`}
      title="Drag to resize · double-click to reset"
    />
  );
}

// Feb 2026 R293s · Inline money cell editor. Debounces commit on blur
// so a user can type a full amount without flooding the backend; empty
// input commits `null` to clear the value. Prefixes the cell with the
// task-row currency symbol so Finance can glance a row and know the
// currency without opening the full Task Finance dialog.
function InlineMoneyInput({ value, sym, onCommit, testid }) {
  const [v, setV] = useState(value == null ? "" : String(value));
  // Sync when parent updates (e.g. optimistic set from another cell).
  useEffect(() => { setV(value == null ? "" : String(value)); }, [value]);
  // Feb 2026 R293s · Escape must revert, never commit. The onKeyDown handler
  // sets setV(original) and then blur(); without this flag the blur reads the
  // STALE closure v (the typed value) and PATCHes it anyway.
  const skipCommitRef = useRef(false);
  const commit = () => {
    if (skipCommitRef.current) { skipCommitRef.current = false; return; }
    const trimmed = v.trim();
    const parsed = trimmed === "" ? null : Number(trimmed);
    const current = value == null ? null : Number(value);
    if (parsed === current) return;
    if (parsed !== null && !Number.isFinite(parsed)) { setV(current == null ? "" : String(current)); return; }
    onCommit(parsed);
  };
  return (
    <span className="inline-flex items-center gap-1 w-full">
      <span className="text-[10px] text-muted-foreground font-mono shrink-0">{sym}</span>
      <input
        type="number"
        step="0.01"
        min="0"
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.currentTarget.blur(); }
          if (e.key === "Escape") {
            skipCommitRef.current = true;
            setV(value == null ? "" : String(value));
            e.currentTarget.blur();
          }
        }}
        className="h-6 w-full text-right text-[11px] font-mono tabular-nums px-1 border border-transparent hover:border-border focus:border-primary rounded-none bg-background focus:outline-none"
        placeholder="0"
        data-testid={testid}
      />
    </span>
  );
}

const FILTERS = [
  { v: "",         label: "All billable" },
  { v: "ready",    label: "Ready to bill" },
  { v: "unbilled", label: "Unbilled" },
  { v: "billed",   label: "Billed" },
  { v: "unpaid",   label: "Unpaid" },
  { v: "paid",     label: "Paid" },
];

export default function TaskBillingView({ wsId, projectId = null, clientId = null, assigneeId = null, assigneeName = null, onOpenTask }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [monthRollup, setMonthRollup] = useState([]);
  // Feb 2026 R292y · Category rollup — same shape as month_rollup but
  // bucketed by (task_category_id, currency). The panel toggles between
  // the two views so Finance can slice revenue either way.
  const [categoryRollup, setCategoryRollup] = useState([]);
  const [rollupOpen, setRollupOpen] = useState(false);
  const [rollupMode, setRollupMode] = useState(() => {
    try { return localStorage.getItem("billing-rollup-mode") || "month"; }
    catch { return "month"; }
  });
  useEffect(() => {
    try { localStorage.setItem("billing-rollup-mode", rollupMode); }
    catch { /* silent */ }
  }, [rollupMode]);
  // R148g · Default to "Unbilled" so PMs land on the actionable list.
  const [status, setStatus] = useState("unbilled");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [packageFilter, setPackageFilter] = useState("");    // "" | package | non_package
  const [missingEstimate, setMissingEstimate] = useState(false);
  const [ageFilter, setAgeFilter] = useState("");            // R148g · "" | "0_15" | "16_30" | "over_30"
  const [readyOnly, setReadyOnly] = useState(false);         // R148g · Ready-to-invoice filter
  // R148g · Three grouping modes with a shared L1 → L2 → tasks structure.
  const [groupBy, setGroupBy] = useState("client");
  const [digestOpen, setDigestOpen] = useState(false);
  const [openKeys, setOpenKeys] = useState(new Set());
  const [taskNotesFor, setTaskNotesFor] = useState(null);
  const [projectNotesFor, setProjectNotesFor] = useState(null);
  // R148g · Bulk selection — a Set of task_ids currently ticked.
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [projectNotes, setProjectNotes] = useState({});     // R148g · projectId → {body, author, created_at, count}
  // Feb 2026 R293p · Finance Report launcher. The dialog itself is
  // reused from PlanningBoard — load the task + project arrays
  // lazily on first open so the Billing view doesn't carry the full
  // dataset on mount.
  const [financeReportOpen, setFinanceReportOpen] = useState(false);
  const [financeReportBundle, setFinanceReportBundle] = useState({ tasks: [], projects: [] });
  const [financeReportLoading, setFinanceReportLoading] = useState(false);

  const openFinanceReport = async () => {
    if (!wsId) return;
    setFinanceReportLoading(true);
    try {
      const params = { limit: 1000 };
      if (projectId) params.project_id = projectId;
      const [tr, pr] = await Promise.all([
        api.get(`/workspaces/${wsId}/tasks-with-risk`, { params }),
        api.get(`/workspaces/${wsId}/projects`).catch(() => ({ data: [] })),
      ]);
      setFinanceReportBundle({
        tasks:    tr.data || [],
        projects: pr.data || [],
      });
      setFinanceReportOpen(true);
    } catch { toast.error("Couldn't load Finance report data"); }
    finally { setFinanceReportLoading(false); }
  };

  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const params = {};
      // Feb 2026 · "Ready to bill" is a client-side filter — backend
      // billing endpoint groups by billed/unbilled/paid; we filter the
      // ready-to-bill flag after fetching the "unbilled" slice (ready
      // tasks are by definition not yet billed).
      if (status && status !== "ready") params.status = status;
      else if (status === "ready") params.status = "unbilled";
      if (q.trim()) params.q = q.trim();
      if (projectId) params.project_id = projectId;
      if (clientId) params.client_id = clientId;
      if (assigneeId) params.assignee_id = assigneeId;
      if (packageFilter) params.package_filter = packageFilter;
      if (missingEstimate) params.missing_estimate = "true";
      const { data } = await api.get(`/workspaces/${wsId}/tasks-billing`, { params });
      let tasks = Array.isArray(data?.tasks) ? data.tasks : [];
      if (status === "ready") tasks = tasks.filter((t) => !!t.ready_to_bill);
      setRows(tasks);
      setSummary(data?.summary || null);
      setMonthRollup(Array.isArray(data?.month_rollup) ? data.month_rollup : []);
      setCategoryRollup(Array.isArray(data?.category_rollup) ? data.category_rollup : []);
      setProjectNotes(data?.project_notes || {});
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load billing data"));
      setRows([]); setSummary(null); setMonthRollup([]); setCategoryRollup([]);
    } finally {
      setLoading(false);
    }
  }, [wsId, projectId, clientId, assigneeId, status, q, packageFilter, missingEstimate]);

  useEffect(() => { load(); }, [load]);

  // Client-side counter for the "Missing estimate" quick-filter chip. The
  // real filter runs server-side, but the chip's badge should show how
  // many are currently visible so the user knows if pressing it will
  // shrink or expand the view.
  const missingEstimateCount = useMemo(
    () => rows.filter((t) => t.billable && (!t.est_hours || t.est_hours === 0)).length,
    [rows],
  );

  const toggle = async (t, field, value) => {
    // Optimistic — mutate local row + summary in place.
    setRows((prev) => prev.map((r) => (r.id === t.id ? { ...r, [field]: value } : r)));
    try {
      await api.patch(`/tasks/${t.id}`, { [field]: value });
      // Refresh summary counters after change (cheap since ws-scoped).
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to update"));
      setRows((prev) => prev.map((r) => (r.id === t.id ? { ...r, [field]: !value } : r)));
    }
  };

  // R148g · Age helpers — compute days since the task became "ready to
  // bill" (completed_at if present, otherwise created_at). Used by the
  // Aging Buckets colour code + the >30d stale flag.
  const NOW = Date.now();
  const ageDays = useCallback((t) => {
    const ts = t.completed_at || t.done_at || t.created_at;
    if (!ts) return null;
    try {
      const d = new Date(ts);
      if (isNaN(d.getTime())) return null;
      return Math.floor((NOW - d.getTime()) / (1000 * 60 * 60 * 24));
    } catch { return null; }
  }, [NOW]);

  const ageBucket = useCallback((age) => {
    if (age == null) return null;
    if (age <= 15) return "fresh";
    if (age <= 30) return "aging";
    return "stale";
  }, []);

  // Filter helpers applied client-side (server filters are the coarse
  // pass, these narrow the visible rows for aging / ready-to-invoice).
  const visibleRows = useMemo(() => {
    let list = rows;
    if (ageFilter) {
      list = list.filter((t) => {
        if (t.billed) return false; // only unbilled ages
        const b = ageBucket(ageDays(t));
        return ageFilter === "0_15" ? b === "fresh"
          : ageFilter === "16_30" ? b === "aging"
          : ageFilter === "over_30" ? b === "stale"
          : true;
      });
    }
    return list;
  }, [rows, ageFilter, ageDays, ageBucket]);

  // R148g · Age bucket counts (across all rows, ignores age filter itself
  // so users know what will happen before they click the chip).
  const ageCounts = useMemo(() => {
    const c = { fresh: 0, aging: 0, stale: 0 };
    for (const t of rows) {
      if (t.billed) continue;
      const b = ageBucket(ageDays(t));
      if (b) c[b] += 1;
    }
    return c;
  }, [rows, ageBucket, ageDays]);

  // R148g · Bulk selection helpers.
  const isSelected = (id) => selected.has(id);
  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const setSelectionForRows = (rowsToToggle, on) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const t of rowsToToggle) { on ? next.add(t.id) : next.delete(t.id); }
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const bulkMark = async (field, value) => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selected);
      await api.post(`/workspaces/${wsId}/tasks-billing/bulk-mark`, {
        task_ids: ids,
        [field]: value,
      });
      const label = field === "ready_to_bill"
        ? (value ? "ready to bill" : "not ready")
        : (value ? field : `un${field}`);
      toast.success(`Marked ${ids.length} task${ids.length === 1 ? "" : "s"} as ${label}`);
      clearSelection();
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk mark failed")); }
    finally { setBulkBusy(false); }
  };

  // R148g · Copy for invoice tool — TSV (tabs) that pastes cleanly into
  // Tally / Zoho / QuickBooks / Google Sheets. Uses the *visible*
  // filtered rows so the user gets exactly what they see.
  const copyForInvoice = async () => {
    const list = visibleRows;
    if (list.length === 0) { toast.info("Nothing to copy — adjust filters"); return; }
    const header = ["Client", "Project", "Task", "Package", "Assignee",
                    "Est. hours", "Hours logged", "Billed", "Paid",
                    "Age (days)", "Task ID"].join("\t");
    const lines = list.map((t) => [
      (t.client_name || "").replace(/\t/g, " "),
      (t.project_name || "").replace(/\t/g, " "),
      (t.title || "").replace(/\t/g, " "),
      (t.package_name || (t.from_package ? "" : "Ad-hoc")).replace(/\t/g, " "),
      (t.assignee_name || "").replace(/\t/g, " "),
      t.est_hours || 0,
      t.total_effort_hours || t.actual_hours || 0,
      t.billed ? "Yes" : "No",
      t.paid ? "Yes" : "No",
      ageDays(t) ?? "",
      t.id,
    ].join("\t"));
    const tsv = [header, ...lines].join("\n");
    try {
      await navigator.clipboard.writeText(tsv);
      toast.success(`Copied ${list.length} rows — paste into Tally / Zoho / Sheets`);
    } catch {
      // Fallback: legacy execCommand for older browsers / non-secure ctx
      const ta = document.createElement("textarea");
      ta.value = tsv; document.body.appendChild(ta);
      ta.select(); document.execCommand("copy"); ta.remove();
      toast.success(`Copied ${list.length} rows`);
    }
  };

  // R69b · CSV export — download the current filter as a CSV file. Uses
  // the axios blob response so the auth header stays attached.
  const exportCsv = async () => {
    if (!wsId) return;
    try {
      const params = {};
      if (status) params.status = status;
      if (q.trim()) params.q = q.trim();
      if (projectId) params.project_id = projectId;
      if (clientId) params.client_id = clientId;
      const resp = await api.get(`/workspaces/${wsId}/tasks-billing.csv`, {
        params,
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 19);
      a.download = `tasks-billing-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} row${rows.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Export failed"));
    }
  };

  const grouped = useMemo(() => {
    // R148g · Three group modes — unified as L1 → L2 → tasks.
    const KEY = (t, dim) => {
      if (dim === "client")  return [t.client_id  || "_none", t.client_name  || "— No client"];
      if (dim === "project") return [t.project_id || "_none", t.project_name || "— No project"];
      return [t.package_id || "_none",
              t.package_name || (t.from_package ? "— (unnamed package)" : "— Ad-hoc (no package)")];
    };
    const l1Dim = groupBy;
    const l2Dim = groupBy === "client" ? "project"
              : groupBy === "project" ? "package"
              : "project";
    const map = new Map();
    for (const t of visibleRows) {
      const [k1, n1] = KEY(t, l1Dim);
      const [k2, n2] = KEY(t, l2Dim);
      if (!map.has(k1)) {
        map.set(k1, {
          key: k1, name: n1, dim: l1Dim,
          subs: new Map(),
          totalHours: 0, taskCount: 0, missingEst: 0,
          unbilledCount: 0, unpaidCount: 0,
          taskIds: [], readyRows: 0,
          project_id: l1Dim === "project" ? (t.project_id || null) : null,
          project_name: l1Dim === "project" ? (t.project_name || null) : null,
        });
      }
      const l1 = map.get(k1);
      l1.totalHours += (t.total_effort_hours || t.actual_hours || 0);
      l1.taskCount += 1;
      l1.taskIds.push(t.id);
      const isReady = !t.billed && (t.est_hours || 0) > 0 && (t.total_effort_hours || t.actual_hours || 0) > 0;
      if (isReady) l1.readyRows += 1;
      if (t.billable && (!t.est_hours || t.est_hours === 0)) l1.missingEst += 1;
      if (!t.billed) l1.unbilledCount += 1;
      if (t.billed && !t.paid) l1.unpaidCount += 1;
      if (!l1.subs.has(k2)) {
        l1.subs.set(k2, {
          key: k2, name: n2, dim: l2Dim, rows: [],
          totalHours: 0, missingEst: 0, taskIds: [],
          project_id: l2Dim === "project" ? (t.project_id || null) : null,
          project_name: l2Dim === "project" ? (t.project_name || null) : null,
        });
      }
      const l2 = l1.subs.get(k2);
      l2.rows.push(t);
      l2.taskIds.push(t.id);
      l2.totalHours += (t.total_effort_hours || t.actual_hours || 0);
      if (t.billable && (!t.est_hours || t.est_hours === 0)) l2.missingEst += 1;
    }
    // Ready-to-invoice = every task in the L1 is unbilled + has estimate
    // + has hours. Signal to Accounts that this group can go out as-is.
    for (const l1 of map.values()) {
      l1.readyToInvoice = l1.taskCount > 0 && l1.readyRows === l1.taskCount;
    }
    return map;
  }, [visibleRows, groupBy]);

  // R148g · When readyOnly is on, remove L1 groups that aren't ready.
  const displayGrouped = useMemo(() => {
    if (!readyOnly) return grouped;
    const filtered = new Map();
    for (const [k, l1] of grouped.entries()) {
      if (l1.readyToInvoice) filtered.set(k, l1);
    }
    return filtered;
  }, [grouped, readyOnly]);

  // R148g · Expand helpers (controlled state).
  const l1Open = (k) => openKeys.has(`L1:${k}`);
  const l2Open = (k1, k2) => openKeys.has(`L2:${k1}/${k2}`);
  const toggleL1 = (k) => setOpenKeys((prev) => {
    const next = new Set(prev); const key = `L1:${k}`;
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  const toggleL2 = (k1, k2) => setOpenKeys((prev) => {
    const next = new Set(prev); const key = `L2:${k1}/${k2}`;
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  const expandAll = () => {
    const next = new Set();
    for (const [k1, l1] of displayGrouped.entries()) {
      next.add(`L1:${k1}`);
      for (const k2 of l1.subs.keys()) next.add(`L2:${k1}/${k2}`);
    }
    setOpenKeys(next);
  };
  const collapseAll = () => setOpenKeys(new Set());
  // Auto-expand every L1 the first time a group set arrives so the user
  // isn't greeted by a wall of collapsed rows.
  useEffect(() => {
    if (displayGrouped.size === 0) return;
    setOpenKeys((prev) => {
      if (prev.size > 0) return prev;
      const next = new Set();
      for (const [k1, l1] of displayGrouped.entries()) {
        next.add(`L1:${k1}`);
        for (const k2 of l1.subs.keys()) next.add(`L2:${k1}/${k2}`);
      }
      return next;
    });
  }, [displayGrouped]);

  const refreshAfterNote = useCallback(() => { load(); }, [load]);

  // Feb 2026 R293t · Resizable billing table columns (shared across
  // every L2 group's <table> instance so a single drag updates all
  // headers at once).
  const colWidthsKey = wsId ? `co_billing_colw_${wsId}` : null;
  const [colWidths, setColWidths] = useState(() => {
    if (!colWidthsKey) return { ...BILLING_COL_DEFAULTS };
    try {
      const raw = localStorage.getItem(colWidthsKey);
      return raw ? { ...BILLING_COL_DEFAULTS, ...JSON.parse(raw) } : { ...BILLING_COL_DEFAULTS };
    } catch { return { ...BILLING_COL_DEFAULTS }; }
  });
  // Feb 2026 R293t · When the workspace switches, reload widths from
  // that workspace's localStorage slot so we don't accidentally
  // overwrite the new workspace's layout with the old one's.
  useEffect(() => {
    if (!colWidthsKey) { setColWidths({ ...BILLING_COL_DEFAULTS }); return; }
    try {
      const raw = localStorage.getItem(colWidthsKey);
      setColWidths(raw ? { ...BILLING_COL_DEFAULTS, ...JSON.parse(raw) } : { ...BILLING_COL_DEFAULTS });
    } catch { setColWidths({ ...BILLING_COL_DEFAULTS }); }
  }, [colWidthsKey]);
  const colResizeRef = useRef(null); // { key, startX, startWidth }
  useEffect(() => {
    const onMove = (e) => {
      const r = colResizeRef.current;
      if (!r) return;
      const dx = e.clientX - r.startX;
      const next = Math.max(32, r.startWidth + dx);
      setColWidths((cur) => ({ ...cur, [r.key]: next }));
    };
    const onUp = () => {
      if (!colResizeRef.current) return;
      colResizeRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      // Feb 2026 R293t · Persist on mouseup (not on every frame) so the
      // localStorage write happens once per drag, not on every pixel.
      if (colWidthsKey) {
        setColWidths((cur) => {
          try { localStorage.setItem(colWidthsKey, JSON.stringify(cur)); } catch { /* ignore */ }
          return cur;
        });
      }
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [colWidthsKey]);
  const startColResize = useCallback((key) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    colResizeRef.current = {
      key,
      startX: e.clientX,
      startWidth: colWidths[key] || BILLING_COL_DEFAULTS[key] || 100,
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [colWidths]);
  const resetOneColWidth = useCallback((key) => () => {
    setColWidths((cur) => {
      const next = { ...cur, [key]: BILLING_COL_DEFAULTS[key] };
      if (colWidthsKey) { try { localStorage.setItem(colWidthsKey, JSON.stringify(next)); } catch { /* ignore */ } }
      return next;
    });
  }, [colWidthsKey]);
  const resetAllColWidths = useCallback(() => {
    setColWidths({ ...BILLING_COL_DEFAULTS });
    if (colWidthsKey) { try { localStorage.setItem(colWidthsKey, JSON.stringify(BILLING_COL_DEFAULTS)); } catch { /* ignore */ } }
  }, [colWidthsKey]);

  return (
    <div className="space-y-3 pb-16" data-testid="task-billing-view">
      {/* R148g · Sticky bulk-action bar at the bottom — appears when
          rows are selected. Bulk Mark billed / paid, plus clear.
          Feb 2026 · Fix bar getting clipped under the AppShell sidebar
          on narrower viewports. We anchor the LEFT edge to the
          sidebar's trailing edge via the `--co-nav-w` CSS variable
          exposed by `.workspace-shell` so the bar never slides
          under the nav regardless of collapsed state. On mobile
          (sidebar is a drawer, not in-flow) we fall back to
          `left-0`. `flex-wrap` + `overflow-x-auto` are belt-and-
          braces for narrow content widths. */}
      {selected.size > 0 && (
        <div
          className="fixed bottom-0 right-0 z-30 border-t-2 border-sky-500 bg-white shadow-2xl py-3 px-4 flex items-center gap-2 flex-wrap overflow-x-auto"
          style={{ left: "var(--billing-bulk-bar-left, 0px)" }}
          data-testid="billing-bulk-bar">
          <span className="text-sm font-bold text-sky-700">{selected.size} selected</span>
          <span className="text-xs text-muted-foreground">Mark all as:</span>
          {/* Feb 2026 · Ready-to-bill batch handoff — PMs tag a wave of
              completed tasks for Finance in one click.  Amber to match
              the per-row Ready chip in the table + Planning board. */}
          <Button size="sm" className="rounded-none bg-amber-500 hover:bg-amber-600 text-white" onClick={() => bulkMark("ready_to_bill", true)} disabled={bulkBusy} data-testid="billing-bulk-mark-ready">
            {bulkBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
            Ready to bill
          </Button>
          <Button size="sm" variant="outline" className="rounded-none border-amber-500 text-amber-700" onClick={() => bulkMark("ready_to_bill", false)} disabled={bulkBusy} data-testid="billing-bulk-unmark-ready">Not ready</Button>
          <Button size="sm" className="rounded-none bg-sky-600 hover:bg-sky-700" onClick={() => bulkMark("billed", true)} disabled={bulkBusy} data-testid="billing-bulk-mark-billed">
            {bulkBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
            Billed
          </Button>
          <Button size="sm" variant="outline" className="rounded-none border-sky-500 text-sky-700" onClick={() => bulkMark("billed", false)} disabled={bulkBusy} data-testid="billing-bulk-unmark-billed">Unbilled</Button>
          <Button size="sm" className="rounded-none bg-emerald-600 hover:bg-emerald-700" onClick={() => bulkMark("paid", true)} disabled={bulkBusy} data-testid="billing-bulk-mark-paid">Paid</Button>
          <Button size="sm" variant="outline" className="rounded-none border-emerald-500 text-emerald-700" onClick={() => bulkMark("paid", false)} disabled={bulkBusy} data-testid="billing-bulk-unmark-paid">Unpaid</Button>
          <span className="ml-auto" />
          <Button size="sm" variant="ghost" className="rounded-none" onClick={clearSelection} data-testid="billing-bulk-clear">
            <X className="w-3.5 h-3.5 mr-1" /> Clear
          </Button>
        </div>
      )}
      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
        <SumCard label="Billable" value={summary?.total ?? "—"} icon={Receipt} />
        <SumCard label="Unbilled" value={summary?.unbilled ?? "—"} accent="text-amber-700" />
        <SumCard label="Billed" value={summary?.billed ?? "—"} accent="text-sky-700" />
        <SumCard label="Unpaid" value={summary?.unpaid ?? "—"} accent="text-rose-700" />
        <SumCard label="Paid" value={summary?.paid ?? "—"} accent="text-emerald-700" icon={CheckCircle2} />
        <SumCard label="Total hours" value={summary?.total_hours ?? "—"} icon={DollarSign} />
      </div>

      {/* Feb 2026 R293p · Finance Report launcher — exposes the same
          deep-dive dialog that lives on the Planning Board so PMs
          don't have to context-switch to drill into the task-level
          finance table from the Tasks > Billing or Project > Tasks >
          Billing surface. */}
      <div className="flex items-center justify-end" data-testid="billing-finance-report-row">
        <Button
          size="sm"
          className="rounded-none bg-amber-500 hover:bg-amber-600 text-white gap-1 text-xs uppercase tracking-widest"
          onClick={openFinanceReport}
          disabled={financeReportLoading}
          data-testid="billing-finance-report-open"
          title="Open the task-level finance report — search, sort, group by project/brand/client, export CSV"
        >
          {financeReportLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span>₹</span>}
          Finance report
        </Button>
      </div>

      {/* Feb 2026 · Month-wise billed-revenue rollup — collapsed by
          default. One row per (billing month, currency) pair showing
          billed revenue, paid revenue and the outstanding gap so
          Finance can scan the pipeline at a glance. Rollup query runs
          server-side and honours project/client/assignee filters but
          ignores the status chip so every month shows regardless of
          what the user is drilling into right now. */}
      {(monthRollup.length > 0 || categoryRollup.length > 0) && (
        <MonthRollupPanel
          open={rollupOpen}
          onToggle={() => setRollupOpen((v) => !v)}
          monthRows={monthRollup}
          categoryRows={categoryRollup}
          mode={rollupMode}
          onModeChange={setRollupMode}
        />
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 border border-border bg-card p-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search title/description…"
          className="h-8 text-xs rounded-none w-full sm:w-56"
          data-testid="billing-search"
        />
        <div className="flex items-center gap-1 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.v || "all"}
              type="button"
              onClick={() => setStatus(f.v)}
              className={`h-8 text-[10px] uppercase tracking-widest px-2 border ${status === f.v ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted/40 border-border"}`}
              data-testid={`billing-filter-${f.v || "all"}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* R148d · Feb 2026 — Package filter (Package / Non-package / All) */}
        <div className="flex items-center gap-1 flex-wrap ml-1" data-testid="billing-package-filter">
          {[
            { v: "", label: "All items" },
            { v: "package", label: "Package only" },
            { v: "non_package", label: "Non-package" },
          ].map((f) => (
            <button
              key={f.v || "any"}
              type="button"
              onClick={() => setPackageFilter(f.v)}
              className={`h-8 text-[10px] uppercase tracking-widest px-2 border ${packageFilter === f.v ? "bg-foreground text-background border-foreground" : "bg-card hover:bg-muted/40 border-border"}`}
              data-testid={`billing-pkg-${f.v || "all"}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* R148g · Aging bucket chips — colour-code unbilled tasks by age. */}
        {[
          { v: "0_15",   label: "≤ 15d",  cls: "border-emerald-500 text-emerald-700 hover:bg-emerald-50", active: "bg-emerald-600 text-white border-emerald-600", count: ageCounts.fresh },
          { v: "16_30",  label: "16–30d", cls: "border-amber-500 text-amber-800 hover:bg-amber-50",     active: "bg-amber-500 text-white border-amber-500",     count: ageCounts.aging },
          { v: "over_30",label: "> 30d",  cls: "border-rose-500 text-rose-700 hover:bg-rose-50",         active: "bg-rose-600 text-white border-rose-600",       count: ageCounts.stale },
        ].map((b) => {
          const is = ageFilter === b.v;
          return (
            <button
              key={b.v}
              type="button"
              onClick={() => setAgeFilter(is ? "" : b.v)}
              className={`h-8 text-[10px] uppercase tracking-widest px-2 border flex items-center gap-1 ${is ? b.active : `bg-card ${b.cls}`}`}
              data-testid={`billing-age-chip-${b.v}`}
              title={`Show unbilled tasks aged ${b.label} — the older it is, the more urgent it is to bill`}
            >
              {b.label}
              {b.count > 0 && (
                <span className={`inline-block px-1 text-[9px] font-bold ${is ? "bg-white/20" : "bg-current text-white"}`}>{b.count}</span>
              )}
            </button>
          );
        })}

        {/* R148g · Ready-to-invoice filter — every task unbilled + estimate + hours */}
        <button
          type="button"
          onClick={() => setReadyOnly((v) => !v)}
          className={`h-8 text-[10px] uppercase tracking-widest px-2 border flex items-center gap-1 ${readyOnly ? "bg-emerald-600 text-white border-emerald-600" : "bg-card hover:bg-emerald-50 border-emerald-500/60 text-emerald-700"}`}
          data-testid="billing-ready-only"
          title="Show only groups where EVERY task is unbilled + has an estimate + has hours — safe to invoice as-is"
        >
          <CheckCircle2 className="w-3 h-3" /> Ready to invoice
        </button>

        {/* R148d · Missing estimate quick-filter chip */}
        <button
          type="button"
          onClick={() => setMissingEstimate((v) => !v)}
          className={`h-8 text-[10px] uppercase tracking-widest px-2 border flex items-center gap-1 ${missingEstimate ? "bg-red-600 text-white border-red-600" : "bg-card hover:bg-red-50 border-red-500/60 text-red-700"}`}
          data-testid="billing-missing-estimate-chip"
          title="Show only billable tasks that are missing an estimate — often the cause of margin leaks"
        >
          Missing estimate
          {!missingEstimate && missingEstimateCount > 0 && (
            <span className="inline-block bg-red-600 text-white px-1 text-[9px] font-bold" data-testid="billing-missing-estimate-count">{missingEstimateCount}</span>
          )}
        </button>

        {/* R148g · Preview / dry-run the Monday missing-estimate digest.
            Admin-gated on the backend — server returns 403 if the user
            lacks finance.view / workspace.manage. */}
        <button
          type="button"
          onClick={() => setDigestOpen(true)}
          className="h-8 text-[10px] uppercase tracking-widest px-2 border border-border bg-card hover:bg-muted/40 flex items-center gap-1"
          data-testid="billing-digest-preview-open"
          title="Preview the Monday digest and optionally send a real / dry-run copy right now"
        >
          <Mail className="w-3 h-3" /> Digest
        </button>

        {/* R148e · Group-by toggle: client / project / package. */}
        <div className="flex items-center gap-1 flex-wrap ml-1" data-testid="billing-group-by">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Group</span>
          {[
            { v: "client", label: "By client" },
            { v: "project", label: "By project" },
            { v: "package", label: "By package" },
          ].map((f) => (
            <button
              key={f.v}
              type="button"
              onClick={() => setGroupBy(f.v)}
              className={`h-8 text-[10px] uppercase tracking-widest px-2 border ${groupBy === f.v ? "bg-sky-600 text-white border-sky-600" : "bg-card hover:bg-muted/40 border-border"}`}
              data-testid={`billing-group-${f.v}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* R148g · Expand / collapse all */}
        <div className="flex items-center gap-1 ml-1" data-testid="billing-expand-controls">
          <button
            type="button"
            onClick={expandAll}
            className="h-8 text-[10px] uppercase tracking-widest px-2 border border-border bg-card hover:bg-muted/40 flex items-center gap-1"
            data-testid="billing-expand-all"
            title="Expand every client / project / package group"
          >
            <ChevronsDown className="w-3 h-3" /> Expand all
          </button>
          <button
            type="button"
            onClick={collapseAll}
            className="h-8 text-[10px] uppercase tracking-widest px-2 border border-border bg-card hover:bg-muted/40 flex items-center gap-1"
            data-testid="billing-collapse-all"
            title="Collapse every group"
          >
            <ChevronsUp className="w-3 h-3" /> Collapse all
          </button>
          <button
            type="button"
            onClick={resetAllColWidths}
            className="h-8 text-[10px] uppercase tracking-widest px-2 border border-border bg-card hover:bg-muted/40 flex items-center gap-1"
            data-testid="billing-reset-columns"
            title="Reset all billing column widths to defaults (double-click a column edge to reset just that one)"
          >
            Reset cols
          </button>
        </div>

        <span className="ml-auto text-[10px] text-muted-foreground">{visibleRows.length} of {rows.length} task{rows.length === 1 ? "" : "s"}</span>
        {/* R148g · Copy TSV for pasting into Tally / Zoho / QuickBooks / Sheets */}
        <button
          type="button"
          onClick={copyForInvoice}
          disabled={visibleRows.length === 0}
          className="h-8 text-[10px] uppercase tracking-widest px-2 border border-border bg-card hover:bg-muted/40 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
          data-testid="billing-copy-invoice"
          title="Copy the visible rows as tab-separated text — paste directly into Tally / Zoho / QuickBooks / Sheets"
        >
          <Copy className="w-3 h-3" /> Copy for invoice
        </button>
        <button
          type="button"
          onClick={() => exportCsv()}
          disabled={rows.length === 0}
          className="h-8 text-[10px] uppercase tracking-widest px-2 border border-border bg-card hover:bg-muted/40 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
          data-testid="billing-export-csv"
          title="Export the current filter as CSV — Client, Project, Task, Hours, Billed, Paid"
        >
          <Download className="w-3 h-3" /> CSV
        </button>
      </div>

      {loading && rows.length === 0 ? (
        <div className="py-10 text-center text-xs text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground border border-dashed border-border">
          <Receipt className="w-8 h-8 mx-auto mb-2 opacity-40" />
          No billable tasks match this filter.
        </div>
      ) : (
        <div className="space-y-3" data-testid="billing-groups">
          {Array.from(displayGrouped.entries()).map(([k1, l1]) => {
            const isOpen1 = l1Open(k1);
            const l1Icon = isOpen1
              ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />;
            // Bulk-select at group level.
            const groupSelectedCount = l1.taskIds.filter((id) => selected.has(id)).length;
            const groupAllSelected = l1.taskIds.length > 0 && groupSelectedCount === l1.taskIds.length;
            const groupSomeSelected = groupSelectedCount > 0 && !groupAllSelected;
            // Project-scope last billing note when this L1 IS a project.
            const l1Note = l1.dim === "project" ? projectNotes[l1.project_id] : null;
            return (
              <div key={k1} className="border border-border bg-card" data-testid={`billing-l1-${l1.dim}-${k1}`}>
                {/* L1 header */}
                <div
                  className={`px-3 py-2 border-b border-border text-xs font-semibold flex items-center gap-2 cursor-pointer select-none ${l1.readyToInvoice ? "bg-emerald-50" : "bg-muted/40"}`}
                  onClick={() => toggleL1(k1)}
                  data-testid={`billing-l1-toggle-${k1}`}
                >
                  {/* Group-level bulk checkbox */}
                  <input
                    type="checkbox"
                    checked={groupAllSelected}
                    ref={(el) => { if (el) el.indeterminate = groupSomeSelected; }}
                    onChange={(e) => { e.stopPropagation(); setSelectionForRows(l1.taskIds.map((id) => ({ id })), e.target.checked); }}
                    onClick={(e) => e.stopPropagation()}
                    className="h-3.5 w-3.5 accent-sky-600 cursor-pointer"
                    data-testid={`billing-l1-select-${k1}`}
                    title={groupAllSelected ? "Deselect all tasks in this group" : "Select all tasks in this group"}
                  />
                  {l1Icon}
                  <span className="flex-1 truncate">{l1.name}</span>
                  <span className="text-[10px] text-muted-foreground font-normal">{l1.taskCount} task{l1.taskCount === 1 ? "" : "s"}</span>
                  <span className="text-[10px] text-muted-foreground font-normal">{l1.totalHours.toFixed(1)}h</span>
                  {l1.readyToInvoice && (
                    <span className="inline-flex items-center gap-1 border border-emerald-500 bg-emerald-100 text-emerald-800 px-1 text-[10px] font-bold uppercase tracking-widest" title="Every task in this group is unbilled + has an estimate + has hours" data-testid={`billing-l1-ready-${k1}`}>
                      <CheckCircle2 className="w-2.5 h-2.5" /> Ready to invoice
                    </span>
                  )}
                  {l1.unbilledCount > 0 && (
                    <span className="inline-block border border-amber-500 bg-amber-100 text-amber-800 px-1 text-[10px] font-bold" title={`${l1.unbilledCount} unbilled`} data-testid={`billing-l1-unbilled-${k1}`}>
                      {l1.unbilledCount} unbilled
                    </span>
                  )}
                  {l1.unpaidCount > 0 && (
                    <span className="inline-block border border-rose-500 bg-rose-100 text-rose-800 px-1 text-[10px] font-bold" title={`${l1.unpaidCount} billed but unpaid`}>
                      {l1.unpaidCount} unpaid
                    </span>
                  )}
                  {l1.missingEst > 0 && (
                    <span className="inline-block border border-red-500 bg-red-100 text-red-700 px-1 text-[10px] font-bold" title={`${l1.missingEst} tasks missing estimate`}>
                      {l1.missingEst} missing est.
                    </span>
                  )}
                  {/* Project last-note summary — "by / when" + hover tooltip
                      for full body. */}
                  {l1.dim === "project" && l1Note && (
                    <span
                      className="hidden md:inline text-[10px] text-muted-foreground italic max-w-[24ch] truncate font-normal"
                      title={l1Note.body}
                      data-testid={`billing-project-last-note-${l1.project_id}`}
                    >
                      · Last note by {l1Note.author_name} · {fmtRelative(l1Note.created_at)}
                    </span>
                  )}
                  {/* Project-level Notes button (only when L1 dim is project). */}
                  {l1.dim === "project" && l1.project_id && (
                    <HoverCard openDelay={200} closeDelay={80}>
                      <HoverCardTrigger asChild>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setProjectNotesFor({ id: l1.project_id, name: l1.project_name || l1.name }); }}
                          className="inline-flex items-center gap-1 border border-primary/40 bg-primary/5 hover:bg-primary/10 text-primary px-1.5 py-0.5 text-[10px]"
                          data-testid={`billing-project-notes-${l1.project_id}`}
                          title="Open Accounts notes for this project"
                        >
                          <MessageSquare className="w-3 h-3" />
                          Notes{l1Note?.count ? ` (${l1Note.count})` : ""}
                        </button>
                      </HoverCardTrigger>
                      {(l1Note?.recent || []).length > 0 && (
                        <HoverCardContent className="w-80 p-2 rounded-none" side="right" align="start" data-testid={`billing-project-notes-hover-${l1.project_id}`}>
                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border pb-1 mb-1">
                            Last {Math.min(4, l1Note.recent.length)} Accounts note{l1Note.recent.length === 1 ? "" : "s"}
                          </div>
                          <ul className="space-y-2">
                            {l1Note.recent.slice(0, 4).map((n, i) => (
                              <li key={i} className="text-[11px] border-l-2 border-primary/40 pl-2">
                                <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-0.5">
                                  <span className="font-medium text-foreground/80 truncate max-w-[16ch]">{n.author_name || "—"}</span>
                                  <span title={n.created_at}>{fmtRelative(n.created_at)}</span>
                                </div>
                                <div className="whitespace-pre-wrap break-words line-clamp-3">{n.body}</div>
                              </li>
                            ))}
                          </ul>
                        </HoverCardContent>
                      )}
                    </HoverCard>
                  )}
                </div>

                {isOpen1 && Array.from(l1.subs.entries()).map(([k2, l2]) => {
                  const isOpen2 = l2Open(k1, k2);
                  const l2Selected = l2.taskIds.filter((id) => selected.has(id)).length;
                  const l2AllSelected = l2.taskIds.length > 0 && l2Selected === l2.taskIds.length;
                  const l2SomeSelected = l2Selected > 0 && !l2AllSelected;
                  const l2Note = l2.dim === "project" ? projectNotes[l2.project_id] : null;
                  return (
                    <div key={k2} className="border-b border-border/40 last:border-b-0" data-testid={`billing-l2-${l2.dim}-${k2}`}>
                      {/* L2 header */}
                      <div
                        className="px-3 py-1 border-b border-border/50 text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/20 flex items-center gap-2 cursor-pointer select-none"
                        onClick={() => toggleL2(k1, k2)}
                        data-testid={`billing-l2-toggle-${k1}-${k2}`}
                      >
                        <input
                          type="checkbox"
                          checked={l2AllSelected}
                          ref={(el) => { if (el) el.indeterminate = l2SomeSelected; }}
                          onChange={(e) => { e.stopPropagation(); setSelectionForRows(l2.taskIds.map((id) => ({ id })), e.target.checked); }}
                          onClick={(e) => e.stopPropagation()}
                          className="h-3.5 w-3.5 accent-sky-600 cursor-pointer"
                          data-testid={`billing-l2-select-${k1}-${k2}`}
                        />
                        {isOpen2 ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        <span className="flex-1 truncate">{l2.name}</span>
                        <span>· {l2.rows.length} task{l2.rows.length === 1 ? "" : "s"}</span>
                        <span>· {l2.totalHours.toFixed(1)}h</span>
                        {l2.missingEst > 0 && (
                          <span className="inline-block border border-red-500 bg-red-100 text-red-700 px-1 text-[9px] font-bold normal-case tracking-normal">
                            {l2.missingEst} missing est.
                          </span>
                        )}
                        {l2.dim === "project" && l2Note && (
                          <span
                            className="hidden md:inline text-[10px] text-muted-foreground italic normal-case tracking-normal max-w-[22ch] truncate"
                            title={l2Note.body}
                            data-testid={`billing-l2-project-last-note-${l2.project_id}`}
                          >
                            · Last note by {l2Note.author_name} · {fmtRelative(l2Note.created_at)}
                          </span>
                        )}
                        {l2.dim === "project" && l2.project_id && (
                          <HoverCard openDelay={200} closeDelay={80}>
                            <HoverCardTrigger asChild>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setProjectNotesFor({ id: l2.project_id, name: l2.project_name || l2.name }); }}
                                className="inline-flex items-center gap-1 border border-primary/40 bg-primary/5 hover:bg-primary/10 text-primary px-1.5 py-0.5 text-[10px] normal-case tracking-normal"
                                data-testid={`billing-l2-project-notes-${l2.project_id}`}
                                title="Open Accounts notes for this project"
                              >
                                <MessageSquare className="w-2.5 h-2.5" /> Notes{l2Note?.count ? ` (${l2Note.count})` : ""}
                              </button>
                            </HoverCardTrigger>
                            {(l2Note?.recent || []).length > 0 && (
                              <HoverCardContent className="w-80 p-2 rounded-none" side="right" align="start" data-testid={`billing-l2-project-notes-hover-${l2.project_id}`}>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border pb-1 mb-1">
                                  Last {Math.min(4, l2Note.recent.length)} Accounts note{l2Note.recent.length === 1 ? "" : "s"}
                                </div>
                                <ul className="space-y-2">
                                  {l2Note.recent.slice(0, 4).map((n, i) => (
                                    <li key={i} className="text-[11px] border-l-2 border-primary/40 pl-2">
                                      <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-0.5">
                                        <span className="font-medium text-foreground/80 truncate max-w-[16ch]">{n.author_name || "—"}</span>
                                        <span title={n.created_at}>{fmtRelative(n.created_at)}</span>
                                      </div>
                                      <div className="whitespace-pre-wrap break-words line-clamp-3 normal-case tracking-normal">{n.body}</div>
                                    </li>
                                  ))}
                                </ul>
                              </HoverCardContent>
                            )}
                          </HoverCard>
                        )}
                      </div>

                      {isOpen2 && (
                        <div className="overflow-x-auto">
                          {(() => {
                            const _billingTableWidth = BILLING_COL_KEYS.reduce((s, k) => s + (colWidths[k] || BILLING_COL_DEFAULTS[k]), 0);
                            return (
                          <table className="text-xs table-fixed" style={{ width: `${_billingTableWidth}px`, minWidth: "100%" }}>
                            <colgroup>
                              {BILLING_COL_KEYS.map((k) => (
                                <col key={k} style={{ width: `${colWidths[k] || BILLING_COL_DEFAULTS[k]}px` }} />
                              ))}
                            </colgroup>
                            <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/10 border-b border-border/40">
                              <tr>
                                {BILLING_COL_KEYS.map((k) => {
                                  const meta = BILLING_COL_META[k];
                                  const alignCls = meta.align === "right" ? "text-right" : meta.align === "center" ? "text-center" : "text-left";
                                  const padCls = (k === "select" || k === "age" || k === "ccy" || k === "est_amt" || k === "paid_amt" || k === "balance") ? "px-2" : "px-3";
                                  return (
                                    <th
                                      key={k}
                                      className={`${alignCls} ${padCls} py-1.5 font-medium relative`}
                                      title={meta.title || undefined}
                                      data-testid={`billing-col-head-${k}`}
                                    >
                                      {k === "select" ? (
                                        <input
                                          type="checkbox"
                                          checked={l2AllSelected}
                                          ref={(el) => { if (el) el.indeterminate = l2SomeSelected; }}
                                          onChange={(e) => setSelectionForRows(l2.taskIds.map((id) => ({ id })), e.target.checked)}
                                          className="h-3.5 w-3.5 accent-sky-600 cursor-pointer"
                                          aria-label="Select all in group"
                                        />
                                      ) : (
                                        <span className="truncate inline-block max-w-full align-middle">{meta.label}</span>
                                      )}
                                      <BillingResizeHandle
                                        colKey={k}
                                        onMouseDown={startColResize(k)}
                                        onReset={resetOneColWidth(k)}
                                      />
                                    </th>
                                  );
                                })}
                              </tr>
                            </thead>
                            <tbody>
                              {l2.rows.map((t) => {
                                const isMissingEst = !!t.billable && (!t.est_hours || t.est_hours === 0);
                                const note = t.last_billing_note;
                                const days = ageDays(t);
                                const bucket = ageBucket(days);
                                const ageBg = t.billed ? "" :
                                  bucket === "stale" ? "bg-rose-50/70" :
                                  bucket === "aging" ? "bg-amber-50/60" :
                                  bucket === "fresh" ? "" : "";
                                const ageChipCls = bucket === "stale" ? "bg-rose-600 text-white border-rose-600"
                                              : bucket === "aging" ? "bg-amber-500 text-white border-amber-500"
                                              : bucket === "fresh" ? "bg-emerald-500 text-white border-emerald-500"
                                              : "border-border text-muted-foreground";
                                const rowSelected = isSelected(t.id);
                                return (
                                <tr key={t.id} className={`border-b border-border/40 hover:bg-muted/20 ${rowSelected ? "bg-sky-50" : (isMissingEst ? "bg-red-50/60" : ageBg)}`} data-testid={`billing-row-${t.id}`}>
                                  <td className="px-2 py-1.5 text-center">
                                    <input
                                      type="checkbox"
                                      checked={rowSelected}
                                      onChange={() => toggleSelected(t.id)}
                                      className="h-3.5 w-3.5 accent-sky-600 cursor-pointer"
                                      data-testid={`billing-row-select-${t.id}`}
                                      aria-label={`Select ${t.title}`}
                                    />
                                  </td>
                                  <td className="px-3 py-1.5">
                                    <button
                                      type="button"
                                      onClick={() => onOpenTask?.(t)}
                                      className="text-left hover:text-primary min-w-0"
                                      data-testid={`billing-open-${t.id}`}
                                    >
                                      <div className="font-medium truncate max-w-[36ch]">{t.title}</div>
                                      {t.due_date && (
                                        <div
                                          className={`text-[10px] uppercase tracking-wide ${
                                            new Date(t.due_date) < new Date() && t.status !== "done"
                                              ? "text-rose-600 font-semibold"
                                              : "text-muted-foreground"
                                          }`}
                                          data-testid={`billing-due-${t.id}`}
                                        >
                                          Due {String(t.due_date).slice(0, 10)}
                                        </div>
                                      )}
                                    </button>
                                  </td>
                                  <td className="px-3 py-1.5" onClick={(e) => e.stopPropagation()}>
                                    <select
                                      value={t.status || "open"}
                                      onChange={async (e) => {
                                        const next = e.target.value;
                                        try {
                                          await api.patch(`/tasks/${t.id}`, { status: next });
                                          // R148k · optimistic — patch local row so the UI reflects immediately
                                          setRows((prev) => prev.map((r) => r.id === t.id ? { ...r, status: next } : r));
                                          toast.success("Status updated");
                                        } catch (err) {
                                          toast.error(apiErrorMessage(err, "Could not update status"));
                                        }
                                      }}
                                      className="h-6 text-[10px] border border-border bg-background px-1 rounded-none uppercase"
                                      data-testid={`billing-status-${t.id}`}
                                    >
                                      {STATUS_OPTS.map((s) => (
                                        <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="px-3 py-1.5 text-muted-foreground truncate" title={t.assignee_name || ""} data-testid={`billing-assignee-${t.id}`}>
                                    {t.assignee_name || "—"}
                                  </td>
                                  <td className="px-3 py-1.5">
                                    {t.from_package && t.package_name ? (
                                      <span className="inline-block border border-primary/50 text-primary bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold truncate max-w-[16ch]" title={t.package_name} data-testid={`billing-pkg-${t.id}`}>
                                        {t.package_name}
                                      </span>
                                    ) : (
                                      <span className="text-muted-foreground text-[10px]">Ad-hoc</span>
                                    )}
                                  </td>
                                  <td className={`px-3 py-1.5 text-right font-mono ${isMissingEst ? "text-red-700 font-bold" : ""}`} data-testid={`billing-est-${t.id}`}>
                                    {t.est_hours != null && t.est_hours !== 0 ? t.est_hours : (
                                      <span className="inline-block border border-red-500 bg-red-100 text-red-700 px-1.5 text-[10px] font-bold" title="Missing estimate — set est_hours on this task to improve margin tracking" data-testid={`billing-est-missing-${t.id}`}>MISSING</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-1.5 text-right font-mono">
                                    {t.total_effort_hours || t.actual_hours || 0}
                                  </td>
                                  <td className="px-2 py-1.5 text-center">
                                    {days == null ? (
                                      <span className="text-muted-foreground/60 text-[10px]">—</span>
                                    ) : (
                                      <span
                                        className={`inline-block border px-1 text-[10px] font-bold ${ageChipCls}`}
                                        title={
                                          bucket === "stale" ? `Stale — ${days} days since ready to bill. Chase this!`
                                          : bucket === "aging" ? `Aging — ${days} days since ready to bill.`
                                          : `Fresh — ${days} days since ready to bill.`
                                        }
                                        data-testid={`billing-age-${t.id}`}
                                      >
                                        {days}d
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-3 py-1.5 text-center">
                                    <input
                                      type="checkbox"
                                      checked={!!t.ready_to_bill}
                                      onChange={(e) => toggle(t, "ready_to_bill", e.target.checked)}
                                      className="h-4 w-4 accent-amber-500 cursor-pointer"
                                      title="Mark Ready to bill — PM hand-off to Finance"
                                      data-testid={`billing-ready-${t.id}`}
                                    />
                                  </td>
                                  <td className="px-3 py-1.5 text-center">
                                    <input
                                      type="checkbox"
                                      checked={!!t.billed}
                                      onChange={(e) => toggle(t, "billed", e.target.checked)}
                                      className="h-4 w-4 accent-sky-600 cursor-pointer"
                                      data-testid={`billing-billed-${t.id}`}
                                    />
                                  </td>
                                  <td className="px-3 py-1.5 text-center">
                                    <input
                                      type="checkbox"
                                      checked={!!t.paid}
                                      disabled={!t.billed}
                                      onChange={(e) => toggle(t, "paid", e.target.checked)}
                                      className="h-4 w-4 accent-emerald-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                                      title={!t.billed ? "Mark as billed first" : ""}
                                      data-testid={`billing-paid-${t.id}`}
                                    />
                                  </td>
                                  {/* Feb 2026 R293s · Currency + money columns surfaced in Billing table */}
                                  <td className="px-2 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
                                    <select
                                      value={t.currency || "INR"}
                                      onChange={async (e) => {
                                        const next = e.target.value;
                                        try {
                                          await api.patch(`/tasks/${t.id}`, { currency: next });
                                          setRows((prev) => prev.map((r) => r.id === t.id ? { ...r, currency: next } : r));
                                        } catch (err) {
                                          toast.error(apiErrorMessage(err, "Could not update currency"));
                                        }
                                      }}
                                      className="h-6 text-[10px] px-1 border border-border rounded-none bg-background"
                                      data-testid={`billing-currency-${t.id}`}
                                      title="Task currency"
                                    >
                                      {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
                                    </select>
                                  </td>
                                  <td className="px-2 py-1.5 text-right" onClick={(e) => e.stopPropagation()}>
                                    <InlineMoneyInput
                                      value={t.est_amount}
                                      sym={symbolFor(t.currency)}
                                      onCommit={async (v) => {
                                        try {
                                          await api.patch(`/tasks/${t.id}`, { est_amount: v });
                                          setRows((prev) => prev.map((r) => r.id === t.id ? { ...r, est_amount: v } : r));
                                        } catch (err) {
                                          toast.error(apiErrorMessage(err, "Could not update estimate"));
                                        }
                                      }}
                                      testid={`billing-est-amt-${t.id}`}
                                    />
                                  </td>
                                  <td className="px-2 py-1.5 text-right" onClick={(e) => e.stopPropagation()}>
                                    <InlineMoneyInput
                                      value={t.paid_amount}
                                      sym={symbolFor(t.currency)}
                                      onCommit={async (v) => {
                                        try {
                                          await api.patch(`/tasks/${t.id}`, { paid_amount: v });
                                          setRows((prev) => prev.map((r) => r.id === t.id ? { ...r, paid_amount: v } : r));
                                        } catch (err) {
                                          toast.error(apiErrorMessage(err, "Could not update paid"));
                                        }
                                      }}
                                      testid={`billing-paid-amt-${t.id}`}
                                    />
                                  </td>
                                  {(() => {
                                    const est = Number(t.est_amount || 0);
                                    const paid = Number(t.paid_amount || 0);
                                    const bal = est - paid;
                                    const sym = symbolFor(t.currency);
                                    const cls = bal > 0 ? "text-rose-700 font-semibold" : bal < 0 ? "text-emerald-700 font-semibold" : "text-muted-foreground";
                                    return (
                                      <td className={`px-2 py-1.5 text-right font-mono tabular-nums text-[11px] ${cls}`} data-testid={`billing-balance-${t.id}`}>
                                        {est === 0 && paid === 0 ? "—" : `${sym} ${new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(bal)}`}
                                      </td>
                                    );
                                  })()}
                                  <td className="px-3 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
                                    <input
                                      type="month"
                                      value={t.billing_month || ""}
                                      onChange={async (e) => {
                                        const next = e.target.value || null;
                                        try {
                                          await api.patch(`/tasks/${t.id}`, { billing_month: next });
                                          setRows((prev) => prev.map((r) => r.id === t.id ? { ...r, billing_month: next } : r));
                                        } catch (err) {
                                          toast.error(apiErrorMessage(err, "Could not update billing month"));
                                        }
                                      }}
                                      className="h-6 text-[11px] px-1 border border-border rounded-none bg-background"
                                      title="Accounting month this bill lands in — set now to lock the bucket, auto-fills from Billed on"
                                      data-testid={`billing-month-${t.id}`}
                                    />
                                  </td>
                                  <td className="px-3 py-1.5 text-[11px]" data-testid={`billing-last-note-${t.id}`}>
                                    {note ? (
                                      <span className="line-clamp-2 break-words" title={note.body}>{note.body}</span>
                                    ) : (
                                      <span className="text-muted-foreground/60">—</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-1.5 text-[11px] text-muted-foreground truncate" data-testid={`billing-last-note-by-${t.id}`}>
                                    {note ? (
                                      <span>{note.author_name}<br /><span className="text-[10px]">{fmtRelative(note.created_at)}</span></span>
                                    ) : "—"}
                                  </td>
                                  <td className="px-3 py-1.5 text-center">
                                    <button
                                      type="button"
                                      onClick={() => setTaskNotesFor(t)}
                                      className="inline-flex items-center gap-1 border border-border hover:border-primary hover:text-primary px-1.5 py-0.5 text-[10px]"
                                      title="Open Accounts notes thread for this task (only visible from Billing)"
                                      data-testid={`billing-task-notes-btn-${t.id}`}
                                    >
                                      <MessageSquarePlus className="w-3 h-3" />
                                      {(t.billing_notes_count || 0) > 0 && (
                                        <span className="font-bold">{t.billing_notes_count}</span>
                                      )}
                                    </button>
                                  </td>
                                </tr>
                                );
                              })}
                            </tbody>
                          </table>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {digestOpen && (
        <DigestPreviewDialog wsId={wsId} onClose={() => setDigestOpen(false)} />
      )}
      {taskNotesFor && (
        <BillingNotesDialog
          wsId={wsId}
          scope="task"
          scopeId={taskNotesFor.id}
          scopeLabel={taskNotesFor.title || taskNotesFor.id}
          onClose={() => setTaskNotesFor(null)}
          onChange={refreshAfterNote}
        />
      )}
      {projectNotesFor && (
        <BillingNotesDialog
          wsId={wsId}
          scope="project"
          scopeId={projectNotesFor.id}
          scopeLabel={projectNotesFor.name || projectNotesFor.id}
          onClose={() => setProjectNotesFor(null)}
        />
      )}
      {/* Feb 2026 R293p · Finance Report dialog — same component the
          Planning Board uses. We lazy-load its data in
          `openFinanceReport` above so the Billing view doesn't carry
          the full task payload on mount. */}
      <FinanceReportDialog
        open={financeReportOpen}
        onClose={() => setFinanceReportOpen(false)}
        tasks={financeReportBundle.tasks}
        projects={financeReportBundle.projects}
        wsId={wsId}
        onOpenTask={(tid) => {
          setFinanceReportOpen(false);
          if (onOpenTask) {
            const t = (financeReportBundle.tasks || []).find((x) => x.id === tid);
            if (t) onOpenTask(t);
          }
        }}
        onOpenNotes={(target) => {
          if (target?.task) setTaskNotesFor(target.task);
          else if (target?.project) setProjectNotesFor(target.project);
        }}
        onPatchTask={async (tid, patch) => {
          try {
            await api.patch(`/tasks/${tid}`, patch);
            setFinanceReportBundle((prev) => ({
              ...prev,
              tasks: (prev.tasks || []).map((t) => t.id === tid ? { ...t, ...patch } : t),
            }));
            await load();
          } catch { toast.error("Couldn't update billing state"); }
        }}
      />
    </div>
  );
}

/**
 * R148g · Feb 2026 — Missing-Estimate Digest Preview + Send-Now dialog.
 * Renders exactly the same HTML that Monday's cron would send, plus the
 * recipient list. Admins can either "Send dry-run" (no email, no
 * send-log) or "Send now" (real send, marks the send-log so the cron
 * doesn't double-send this week).
 */
function DigestPreviewDialog({ wsId, onClose }) {
  const [state, setState] = useState({ loading: true, data: null });
  const [dryRunBusy, setDryRunBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [postSend, setPostSend] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/workspaces/${wsId}/digests/missing-estimate/preview`);
        if (!cancel) setState({ loading: false, data });
      } catch (e) {
        if (!cancel) {
          toast.error(apiErrorMessage(e, "Preview failed"));
          setState({ loading: false, data: null });
        }
      }
    })();
    return () => { cancel = true; };
  }, [wsId]);

  const run = async ({ dryRun }) => {
    (dryRun ? setDryRunBusy : setSendBusy)(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/digests/missing-estimate/send-now?dry_run=${dryRun}&force=true`);
      setPostSend(data);
      toast.success(dryRun
        ? `Dry-run OK · would send to ${(data.would_send_to || []).length} admin(s)`
        : `Sent · ${(data.sent_to || []).length} recipient(s)`);
    } catch (e) { toast.error(apiErrorMessage(e, "Send failed")); }
    finally { (dryRun ? setDryRunBusy : setSendBusy)(false); }
  };

  const d = state.data;
  const noRecipients = d && (!d.recipients || d.recipients.length === 0);
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-4xl w-[95vw] max-h-[92vh] overflow-y-auto rounded-none"
        data-testid="digest-preview-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" /> Missing-estimate digest · Preview
          </DialogTitle>
          <DialogDescription>
            This is exactly what fires every Monday morning to workspace
            admins with finance / workspace-manage permissions.
          </DialogDescription>
        </DialogHeader>

        {state.loading ? (
          <div className="p-8 text-sm text-muted-foreground text-center">
            <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" /> Building preview…
          </div>
        ) : !d ? (
          <div className="p-8 text-sm text-red-600 text-center">Could not build preview.</div>
        ) : (
          <div className="space-y-4">
            {/* Meta strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="digest-preview-meta">
              <MetaCard label="Tasks flagged" value={d.count} tone={d.count > 0 ? "text-amber-700" : "text-emerald-700"} />
              <MetaCard label="Recipients" value={(d.recipients || []).length} tone={noRecipients ? "text-red-700" : ""} />
              <MetaCard label="Week" value={d.week_key} />
              <MetaCard label="Already fired this week" value={d.already_sent_this_week ? "Yes" : "No"} tone={d.already_sent_this_week ? "text-amber-700" : "text-emerald-700"} />
            </div>

            {noRecipients && (
              <div className="border border-red-500/40 bg-red-50 text-red-800 px-3 py-2 text-xs" data-testid="digest-no-recipients">
                No workspace member has <b>finance.view</b>, <b>finance.manage</b>, <b>workspace.admin</b> or <b>workspace.manage</b>.
                Grant one of these before enabling the digest.
              </div>
            )}

            {/* Recipient list */}
            {(d.recipient_users || []).length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Will send to</div>
                <div className="flex flex-wrap gap-1" data-testid="digest-preview-recipients">
                  {(d.recipient_users || []).map((u) => (
                    <span key={u.id} className="text-[10px] px-1.5 py-0.5 border border-border bg-muted/40" data-testid={`digest-preview-recipient-${u.id}`}>
                      {u.name} · {u.email}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Rendered HTML preview */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Rendered email</div>
              <div
                className="border-2 border-dashed border-border bg-white p-4 max-h-96 overflow-y-auto"
                data-testid="digest-preview-html"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: d.html }}
              />
              <div className="text-[10px] text-muted-foreground mt-1">
                Subject: <span className="font-mono">{d.subject}</span>
              </div>
            </div>

            {postSend && (
              <div className="border border-emerald-500/40 bg-emerald-50 text-emerald-900 p-3 text-xs" data-testid="digest-post-send-result">
                <div className="font-bold">
                  {postSend.status === "sent" && "Sent successfully"}
                  {postSend.status === "dry_run" && "Dry-run complete"}
                  {postSend.status === "skipped_empty" && "Nothing to send — no missing estimates"}
                  {postSend.status === "skipped_already_sent_this_week" && "Skipped — already fired this week"}
                  {postSend.status === "send_failed" && "Send failed"}
                </div>
                {postSend.would_send_to && postSend.would_send_to.length > 0 && (
                  <div className="mt-1">Would send to: {postSend.would_send_to.join(", ")}</div>
                )}
                {postSend.sent_to && postSend.sent_to.length > 0 && (
                  <div className="mt-1">Sent to: {postSend.sent_to.join(", ")}</div>
                )}
                {postSend.error && <div className="mt-1 text-red-700">{postSend.error}</div>}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Close</Button>
          <Button
            variant="outline"
            className="rounded-none"
            onClick={() => run({ dryRun: true })}
            disabled={dryRunBusy || sendBusy || !d || d.count === 0}
            data-testid="digest-preview-dry-run"
          >
            {dryRunBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
            Send dry-run
          </Button>
          <Button
            className="rounded-none"
            onClick={() => run({ dryRun: false })}
            disabled={dryRunBusy || sendBusy || !d || d.count === 0 || noRecipients}
            data-testid="digest-preview-send-now"
          >
            {sendBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1" />}
            Send now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MetaCard({ label, value, tone }) {
  return (
    <div className="border border-border p-2 bg-background">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-base font-bold tabular-nums ${tone || ""}`}>{value ?? "—"}</div>
    </div>
  );
}

function SumCard({ label, value, accent = "text-foreground", icon: Icon }) {
  return (
    <div className="border border-border bg-card p-2">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`text-lg font-bold ${accent}`}>{value}</div>
    </div>
  );
}

/**
 * Feb 2026 · Month-wise billed-revenue rollup for the Billing tab.
 * Collapsed by default — the header shows the aggregated totals per
 * currency so Finance can glance even without expanding. Each row
 * shows Billed, Paid, Outstanding for one (month, currency) pair.
 *
 * Feb 2026 R292y · Also handles the Category rollup via a toggle in
 * the header. `monthRows` + `categoryRows` are both passed in; the
 * active `mode` picks which one to render. CSV export sends the
 * same `group_by` so finance downloads what they see.
 */
function MonthRollupPanel({ open, onToggle, monthRows = [], categoryRows = [],
                             mode = "month", onModeChange }) {
  const rows = mode === "category" ? categoryRows : monthRows;
  // Grab the current filter context from the URL — the rollup CSV
  // endpoint accepts the same project / client / assignee params so
  // the export matches what Finance is looking at.
  const _ws = (typeof window !== "undefined"
    ? localStorage.getItem("activeWorkspaceId") : null);
  const _exportCsv = async () => {
    try {
      if (!_ws) return;
      const sp = new URLSearchParams(window.location.search);
      const q = new URLSearchParams();
      ["project_id", "client_id", "assignee_id"].forEach((k) => {
        const v = sp.get(k);
        if (v) q.set(k, v);
      });
      q.set("group_by", mode);
      const url = `/workspaces/${_ws}/tasks-billing/month-rollup.csv`
                  + (q.toString() ? `?${q.toString()}` : "");
      const r = await api.get(url, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv" });
      const link = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      link.href = URL.createObjectURL(blob);
      link.download = `billing-${mode}-rollup-${stamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(`${mode === "category" ? "Category" : "Month"} rollup CSV downloaded`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Export failed"));
    }
  };
  const totalsByCcy = useMemo(() => {
    const acc = {};
    for (const r of rows) {
      const k = r.currency || "INR";
      if (!acc[k]) acc[k] = { currency: k, billed: 0, paid: 0, unpaid: 0, buckets: 0 };
      acc[k].billed += Number(r.billed_amount || 0);
      acc[k].paid += Number(r.paid_amount || 0);
      acc[k].unpaid += Number(r.unpaid_amount || 0);
      acc[k].buckets += 1;
    }
    return Object.values(acc);
  }, [rows]);

  const fmt = (n, ccy) => {
    try {
      return new Intl.NumberFormat("en-IN", {
        style: "currency", currency: ccy || "INR",
        maximumFractionDigits: 0,
      }).format(Number(n || 0));
    } catch {
      return `${ccy || ""} ${Number(n || 0).toLocaleString()}`;
    }
  };
  const fmtMonth = (ym) => {
    if (!ym || ym.length < 7) return ym || "—";
    const [y, m] = ym.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleString("en-US", { month: "short", year: "numeric" });
  };

  return (
    <div className="border border-border bg-card" data-testid="billing-month-rollup">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted/30"
        data-testid="billing-month-rollup-toggle"
      >
        {open
          ? <ChevronDown className="w-3.5 h-3.5" />
          : <ChevronRight className="w-3.5 h-3.5" />}
        <span className="font-semibold uppercase tracking-widest text-[10px]">
          Billing rollup
        </span>
        {/* Feb 2026 R292y · Mode toggle. stopPropagation so clicking a
            tab doesn't collapse the panel at the same time. */}
        <span className="flex items-center ml-1 border border-border rounded-none" onClick={(e) => e.stopPropagation()}>
          {[
            { k: "month",    label: "Month" },
            { k: "category", label: "Category" },
          ].map(({ k, label }) => (
            <span
              key={k}
              role="button"
              tabIndex={0}
              onClick={() => onModeChange?.(k)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault(); onModeChange?.(k);
                }
              }}
              className={`px-2 py-0.5 text-[10px] font-medium cursor-pointer ${
                mode === k
                  ? "bg-foreground text-background"
                  : "bg-background hover:bg-muted/40"
              }`}
              data-testid={`billing-rollup-mode-${k}`}
            >
              {label}
            </span>
          ))}
        </span>
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground text-[11px]">
          {rows.length} {mode === "category"
            ? (rows.length === 1 ? "category" : "categories")
            : (rows.length === 1 ? "month" : "months")}
        </span>
        <span className="ml-auto flex items-center gap-3 flex-wrap">
          {totalsByCcy.map((t) => (
            <span key={t.currency} className="flex items-center gap-2 text-[11px]"
                  data-testid={`billing-month-rollup-ccy-${t.currency}`}>
              <span className="font-mono text-muted-foreground">{t.currency}</span>
              <span><span className="text-muted-foreground">Billed</span>{" "}
                <b className="text-sky-700">{fmt(t.billed, t.currency)}</b></span>
              <span><span className="text-muted-foreground">Paid</span>{" "}
                <b className="text-emerald-700">{fmt(t.paid, t.currency)}</b></span>
              <span><span className="text-muted-foreground">Outstanding</span>{" "}
                <b className="text-rose-700">{fmt(t.unpaid, t.currency)}</b></span>
            </span>
          ))}
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); _exportCsv(); }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault(); e.stopPropagation(); _exportCsv();
              }
            }}
            className="flex items-center gap-1 border border-border px-2 py-1 text-[11px] font-medium hover:bg-muted/40 cursor-pointer"
            data-testid="billing-month-rollup-export"
            title={`Download ${mode === "category" ? "category" : "month"}-wise billed CSV (task rows under each ${mode === "category" ? "category" : "month"})`}
          >
            <Download className="w-3 h-3" /> Export CSV
          </span>
        </span>
      </button>
      {open && (
        <div className="border-t border-border overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/10 border-b border-border/40">
              <tr>
                <th className="text-left px-3 py-1.5 font-medium w-32">{mode === "category" ? "Category" : "Month"}</th>
                <th className="text-left px-3 py-1.5 font-medium w-20">Currency</th>
                <th className="text-right px-3 py-1.5 font-medium w-20">Tasks</th>
                <th className="text-right px-3 py-1.5 font-medium">Billed</th>
                <th className="text-right px-3 py-1.5 font-medium">Paid</th>
                <th className="text-right px-3 py-1.5 font-medium">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const key = mode === "category"
                  ? `${r.task_category_id || "none"}-${r.currency}`
                  : `${r.month}-${r.currency}`;
                const label = mode === "category"
                  ? (r.category_name || "Uncategorised")
                  : fmtMonth(r.month);
                return (
                  <tr key={key}
                      className="border-b border-border/40 hover:bg-muted/20"
                      data-testid={`billing-rollup-row-${key}`}>
                    <td className="px-3 py-1.5 font-medium">{label}</td>
                    <td className="px-3 py-1.5 font-mono text-muted-foreground">{r.currency}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.task_count}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-sky-700 font-semibold">
                      {fmt(r.billed_amount, r.currency)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-emerald-700">
                      {fmt(r.paid_amount, r.currency)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-rose-700">
                      {fmt(r.unpaid_amount, r.currency)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-muted/20 border-t border-border/60">
              {totalsByCcy.map((t) => (
                <tr key={`total-${t.currency}`}
                    data-testid={`billing-month-rollup-total-${t.currency}`}>
                  <td className="px-3 py-1.5 font-bold uppercase tracking-widest text-[10px]"
                      colSpan={2}>Total · {t.currency}</td>
                  <td className="px-3 py-1.5 text-right text-muted-foreground">—</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-bold text-sky-700">
                    {fmt(t.billed, t.currency)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-bold text-emerald-700">
                    {fmt(t.paid, t.currency)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-bold text-rose-700">
                    {fmt(t.unpaid, t.currency)}
                  </td>
                </tr>
              ))}
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
